import { Injectable, inject } from '@angular/core';
import { forkJoin, map, Observable, catchError, of } from 'rxjs';
import { MealService } from '../core/services/meal.service';
import { ActivityService } from '../core/services/activity.service';
import { EventService } from './event.service';
import { generateDateOptions, getImageUrl } from '../shared/utils/commenFunctions';
import { FormDateUtils } from '../shared/utils/form-date-utils';
import { getProgramId } from '../shared/utils/storage-utils';
import { MealDataItem, MealPreference } from '../core/models/meal.model';

export interface DailySummaryActivity {
  id: string;
  name: string;
  groupName?: string;
  /** Resolved URL for the activity thumbnail (empty when missing). */
  image: string;
}

export interface DailySummaryEvent {
  id: string;
  title: string;
  /** Short date label for cards (e.g. "12 December"). */
  date: string;
  description?: string;
  image?: string;
  /** ISO calendar date when parseable (YYYY-MM-DD), else empty. */
  dateValue?: string;
  isRegistered?: boolean;
  isWaitlisted?: boolean;
  waitlistedCount?: number;
}

export interface DailySummaryPlanDay {
  dateValue: string;
  dateLabel: string;
  dayLabel: string;
  isToday: boolean;
  mealScheduled: boolean;
  /** True when this date has a meal booking (same set as green date pills). */
  mealBooked: boolean;
  /** Figma meal line (e.g. "Hot Meal", "Cold Meal", "None"). */
  mealLabel: string;
  transportScheduled: boolean;
  /** Display value for transport on the day card (e.g. "Round trip"). */
  transportLabel: string;
  activitiesLabel: string;
  /** Individual activity names for multi-line card display. */
  activityNames: string[];
  activitiesScheduled: boolean;
  /** Registered event titles for this calendar day. */
  eventNames: string[];
  eventsLabel: string;
  eventsScheduled: boolean;
}

export interface DailySummary {
  mealType?: string;
  drink?: string;
  transport?: string;
  locationName?: string;
  activities: DailySummaryActivity[];
  events: DailySummaryEvent[];
  hasMealSelection: boolean;
  hasTransportSelection: boolean;
  bookedMealDateLabels: string[];
  /** Today + upcoming weekdays for the success / Edit Plan list view. */
  planDays: DailySummaryPlanDay[];
}

@Injectable({
  providedIn: 'root',
})
export class DailySummaryService {
  private readonly mealService = inject(MealService);
  private readonly activityService = inject(ActivityService);
  private readonly eventService = inject(EventService);

  loadSummary(userId: number): Observable<DailySummary> {
    const dateOptions = generateDateOptions(4);
    const todayDate = dateOptions[0]?.dateValue ?? this.getTodayIsoDate();
    const allDates = dateOptions
      .map((option) => option.dateValue)
      .filter((value): value is string => !!value);

    const activityRequests =
      allDates.length > 0
        ? allDates.map((date) =>
            this.activityService.getActivities({ index: 0, limit: 100, userid: userId, date }).pipe(
              map((response) => ({
                date,
                activities: (response.activities ?? [])
                  .filter((activity) => (activity as { isSubmitted?: boolean }).isSubmitted === true)
                  .map((activity) => {
                    const rawImage = (activity.activityImage ?? '').trim();
                    return {
                      id: activity.id,
                      name: activity.activityName,
                      groupName: activity.groupName,
                      image: rawImage ? getImageUrl(rawImage) : '',
                    } as DailySummaryActivity;
                  }),
              })),
              catchError(() => of({ date, activities: [] as DailySummaryActivity[] }))
            )
          )
        : [
            this.activityService
              .getActivities({ index: 0, limit: 100, userid: userId, date: todayDate })
              .pipe(
                map((response) => ({
                  date: todayDate,
                  activities: (response.activities ?? [])
                    .filter((activity) => (activity as { isSubmitted?: boolean }).isSubmitted === true)
                    .map((activity) => {
                      const rawImage = (activity.activityImage ?? '').trim();
                      return {
                        id: activity.id,
                        name: activity.activityName,
                        groupName: activity.groupName,
                        image: rawImage ? getImageUrl(rawImage) : '',
                      } as DailySummaryActivity;
                    }),
                })),
                catchError(() => of({ date: todayDate, activities: [] as DailySummaryActivity[] }))
              ),
          ];

    return forkJoin({
      meal: this.mealService
        .getMealInfo({
          userId,
          date: allDates.length ? allDates : [todayDate],
          programId: getProgramId(),
        })
        .pipe(
          map((response) => this.parseMealSummary(response?.results, dateOptions, todayDate)),
          catchError(() => of(this.parseMealSummary(undefined, dateOptions, todayDate)))
        ),
      activitiesByDate: forkJoin(activityRequests).pipe(
        map((rows) => {
          const mapByDate = new Map<string, DailySummaryActivity[]>();
          for (const row of rows) {
            mapByDate.set(row.date, row.activities);
          }
          return mapByDate;
        }),
        catchError(() => of(new Map<string, DailySummaryActivity[]>()))
      ),
      events: this.eventService.getUpcomingEvents(0, 50, userId).pipe(
        map((events) =>
          events
            .filter((event) => event.isRegistered === true || event.isWaitlisted === true)
            .map((event) => {
              const dateLabel = (event.date ?? '').trim();
              return {
                id: event.id,
                title: event.title,
                date: dateLabel || (event.dateTime ?? '').trim(),
                description: (event.description ?? '').trim(),
                image: (event.image ?? '').trim(),
                dateValue: this.resolveEventDateValue(dateLabel, event.dateTime, allDates),
                isRegistered: event.isRegistered === true,
                isWaitlisted: event.isWaitlisted === true,
                waitlistedCount: event.waitlistedCount ?? 0,
              } as DailySummaryEvent;
            })
        ),
        catchError(() => of([] as DailySummaryEvent[]))
      ),
    }).pipe(
      map(({ meal, activitiesByDate, events }) => {
        const todayActivities = activitiesByDate.get(todayDate) ?? [];
        const planDays = (meal.planDays ?? []).map((day) => {
          const dayActivities = activitiesByDate.get(day.dateValue) ?? [];
          const activityNames = dayActivities
            .map((a) => a.name?.trim())
            .filter((name): name is string => !!name);
          const activitiesLabel =
            activityNames.length === 0
              ? 'None'
              : activityNames.length === 1
                ? activityNames[0]
                : `${activityNames.slice(0, -1).join(', ')} and ${activityNames[activityNames.length - 1]}`;

          const dayEvents = events.filter(
            (event) =>
              !!event.dateValue &&
              event.dateValue === day.dateValue,
          );
          const eventNames = dayEvents
            .map((event) => event.title?.trim())
            .filter((title): title is string => !!title);
          const eventsLabel =
            eventNames.length === 0
              ? 'None'
              : eventNames.length === 1
                ? eventNames[0]
                : `${eventNames.slice(0, -1).join(', ')} and ${eventNames[eventNames.length - 1]}`;

          return {
            ...day,
            activitiesLabel,
            activityNames,
            activitiesScheduled: activityNames.length > 0,
            eventNames,
            eventsLabel,
            eventsScheduled: eventNames.length > 0,
          };
        });

        return {
          ...meal,
          activities: todayActivities,
          events,
          planDays,
        };
      })
    );
  }

  /**
   * Map event display date ("12 December" / "12 December, 7:00 pm") onto an ISO day
   * from the current plan window when possible.
   */
  private resolveEventDateValue(
    dateLabel: string,
    dateTime: string | undefined,
    planDates: string[],
  ): string {
    const raw = (dateLabel || dateTime || '').split(',')[0]?.trim() ?? '';
    if (!raw || planDates.length === 0) {
      return '';
    }

    for (const iso of planDates) {
      const date = new Date(`${iso}T12:00:00`);
      if (Number.isNaN(date.getTime())) {
        continue;
      }
      const longLabel = date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
      });
      const shortLabel = date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
      });
      const normalizedRaw = raw.toLowerCase();
      if (
        normalizedRaw === longLabel.toLowerCase() ||
        normalizedRaw === shortLabel.toLowerCase() ||
        normalizedRaw.includes(longLabel.toLowerCase()) ||
        normalizedRaw.includes(shortLabel.toLowerCase())
      ) {
        return iso;
      }
    }

    return '';
  }

  private parseMealSummary(
    results?: MealDataItem[],
    dateOptions: ReturnType<typeof generateDateOptions> = generateDateOptions(4),
    todayDate?: string
  ): DailySummary {
    const items = results ?? [];
    const normalizedToday = this.normalizeDateKey(todayDate);
    const todayItem =
      items.find((item) => this.normalizeDateKey(item.mealDate ?? item.date) === normalizedToday) ??
      items[0];

    const chipByDate = new Map<string, string>();
    const mealByDate = new Map<string, ReturnType<DailySummaryService['parseMealPreferences']>>();
    let hasMealSelection = false;
    let hasTransportSelection = false;

    for (const item of items) {
      const parsed = this.parseMealPreferences(item);
      const dateKey = this.normalizeDateKey(item.mealDate ?? item.date);
      if (dateKey) {
        mealByDate.set(dateKey, parsed);
      }

      if (parsed.hasTransportSelection) {
        hasTransportSelection = true;
      }

      // Chip a date when a meal preference exists, or the meal row was explicitly submitted.
      if ((!parsed.hasMealSelection && item.submitted !== true) || !dateKey) {
        continue;
      }

      hasMealSelection = true;
      const option = dateOptions.find((d) => d.dateValue === dateKey);
      const chipLabel = this.formatChipLabel(option?.dateLabel ?? this.toShortChipLabel(dateKey));
      if (chipLabel) {
        chipByDate.set(dateKey, chipLabel);
      }
    }

    const bookedMealDateLabels = [...chipByDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, label]) => label);

    const todayParsed = this.parseMealPreferences(todayItem);
    const todayKey = normalizedToday || dateOptions[0]?.dateValue || '';

    // Only dates with a meal booking get a card on the done screen (matches green pills).
    const planDays: DailySummaryPlanDay[] = [...chipByDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateValue, dateLabel]) => {
        const option = dateOptions.find((d) => d.dateValue === dateValue);
        const parsed = mealByDate.get(dateValue);
        const mealType = (parsed?.mealType ?? '').trim();
        const mealScheduled = !!mealType && mealType.toLowerCase() !== 'no meal';
        const transportRaw = (parsed?.transport ?? '').trim();
        // Show any saved transport value (including Not Required); only use None when absent.
        const transportScheduled = !!transportRaw;
        const transportLabel = transportScheduled
          ? this.formatTransportLabel(transportRaw)
          : 'None';
        const isToday = dateValue === todayKey;
        return {
          dateValue,
          dateLabel,
          dayLabel: option?.dayLabel ?? '',
          isToday,
          mealScheduled,
          mealBooked: true,
          mealLabel: this.formatMealLabel(mealType),
          transportScheduled,
          transportLabel,
          activitiesLabel: 'None',
          activityNames: [] as string[],
          activitiesScheduled: false,
          eventNames: [] as string[],
          eventsLabel: 'None',
          eventsScheduled: false,
        };
      });

    return {
      mealType: todayParsed.mealType,
      drink: todayParsed.drink,
      transport: todayParsed.transport,
      locationName: todayItem?.locationName,
      activities: [],
      events: [],
      hasMealSelection,
      hasTransportSelection,
      bookedMealDateLabels,
      planDays,
    };
  }

  /** Figma chips use short date without trailing period, e.g. "12 Dec". */
  private formatChipLabel(label: string): string {
    const trimmed = (label ?? '').trim();
    if (!trimmed) {
      return '';
    }
    return trimmed.endsWith('.') ? trimmed.slice(0, -1) : trimmed;
  }

  private formatTransportLabel(value: string): string {
    const trimmed = value.trim();
    if (!trimmed || /^none$/i.test(trimmed) || /^no\s*need$/i.test(trimmed)) {
      return 'None';
    }
    if (/^not\s*required$/i.test(trimmed)) {
      return 'Not Required';
    }
    // Prefer Figma-style "Round trip" casing when we know the option.
    if (/^round\s*trip$/i.test(trimmed)) {
      return 'Round trip';
    }
    if (/^to\s*center$/i.test(trimmed)) {
      return 'To Center';
    }
    if (/^to\s*home$/i.test(trimmed)) {
      return 'To Home';
    }
    return trimmed;
  }

  /** Figma meal line: "Hot Meal" / "Cold Meal" / "None". */
  private formatMealLabel(mealType: string): string {
    const trimmed = (mealType ?? '').trim();
    if (!trimmed || trimmed.toLowerCase() === 'no meal' || trimmed.toLowerCase() === 'none') {
      return 'None';
    }
    if (/^hot(\s+meal)?$/i.test(trimmed)) {
      return 'Hot Meal';
    }
    if (/^cold(\s+meal)?$/i.test(trimmed)) {
      return 'Cold Meal';
    }
    return trimmed;
  }

  private parseMealPreferences(item?: MealDataItem): Pick<
    DailySummary,
    'mealType' | 'drink' | 'transport' | 'hasMealSelection' | 'hasTransportSelection'
  > {
    const prefs = this.normalizeMealPreferences(item?.mealPreference);
    if (prefs.length === 0) {
      return {
        hasMealSelection: false,
        hasTransportSelection: false,
      };
    }

    let mealType: string | undefined;
    let drink: string | undefined;
    let transport: string | undefined;

    for (const pref of prefs) {
      if (pref.preference) {
        mealType = pref.preference;
      }
      if (pref.drinks) {
        drink = pref.drinks;
      }
      if (pref.transport) {
        transport = pref.transport;
      }
    }

    const hasMealSelection = !!mealType;
    const hasTransportSelection =
      !!transport && transport !== 'none' && transport !== 'No Need' && transport !== 'Not Required' && transport !== 'Not required';

    return {
      mealType,
      drink,
      transport,
      hasMealSelection,
      hasTransportSelection,
    };
  }

  /** Accept array, single object, or JSON string from meals/get. */
  private normalizeMealPreferences(raw: unknown): MealPreference[] {
    if (raw == null) {
      return [];
    }

    let value: unknown = raw;
    if (typeof raw === 'string') {
      try {
        value = JSON.parse(raw);
      } catch {
        return [];
      }
    }

    if (Array.isArray(value)) {
      return value.filter((item): item is MealPreference => !!item && typeof item === 'object');
    }

    if (typeof value === 'object') {
      return [value as MealPreference];
    }

    return [];
  }

  /** Normalize API dates like "2026-09-17" or "2026-09-17T00:00:00" to YYYY-MM-DD. */
  private normalizeDateKey(value?: string | null): string {
    if (!value) {
      return '';
    }
    const trimmed = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return trimmed.slice(0, 10);
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /** Fallback chip label e.g. "17 Sep" when date is outside generateDateOptions window. */
  private toShortChipLabel(dateKey: string): string {
    const date = new Date(`${dateKey}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return dateKey;
    }
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate()} ${monthNames[date.getMonth()]}`;
  }

  private getTodayIsoDate(): string {
    return FormDateUtils.formatHoustonIsoDate();
  }
}
